import { strFromU8, unzipSync } from 'fflate';

/**
 * Text gewinnen, wo Text ist (docs/KONZEPT.md, Abschnitt 19.2).
 *
 * Der Satz spart die meiste Arbeit im ganzen Bereich: Eine verschickte Rechnung ist
 * ein PDF mit Textschicht, ein Word-Dokument ist ein ZIP mit XML, eine E-Mail ist
 * schlicht Text. Erkannt werden muss nur, was tatsächlich Pixel sind — und bei allem
 * Digitalen entsteht damit gar kein Erkennungsfehler.
 *
 * Läuft vollständig im Browser. Nichts hiervon verlässt das Gerät.
 */

export type Quelle = 'text' | 'pdf' | 'docx' | 'eml' | 'ocr' | 'claude' | 'keine';

export type Extracted = {
  text: string;
  quelle: Quelle;
  /** Datum aus dem Dokument, nicht das des Eingangs. */
  datum: number | null;
  /** Vorschlag für die Überschrift — Betreff, erste Zeile, sonst der Dateiname. */
  titel: string | null;
};

/** Unter so wenig Text je Seite ist ein PDF ein Scan und kein Schriftstück. */
const TEXT_PER_PAGE = 40;

const decoder = new TextDecoder();

function tidy(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Erste Zeile mit Substanz — als Überschriftsvorschlag. */
export function firstLine(text: string): string | null {
  const line = text
    .split('\n')
    .map((entry) => entry.trim())
    .find((entry) => entry.length > 3);
  return line ? line.slice(0, 80) : null;
}

const MONATE: Record<string, number> = {
  januar: 1, februar: 2, märz: 3, maerz: 3, april: 4, mai: 5, juni: 6, juli: 7,
  august: 8, september: 9, oktober: 10, november: 11, dezember: 12,
};

/**
 * Das Datum *im* Dokument. Gesucht wird der erste plausible Treffer; was daneben
 * liegt, korrigiert man von Hand — ein Vorschlag ist besser als ein leeres Feld.
 */
export function findDate(text: string, now = new Date()): number | null {
  const window = text.slice(0, 4_000);
  const candidates: number[] = [];

  const push = (year: number, month: number, day: number): void => {
    if (month < 1 || month > 12 || day < 1 || day > 31) return;
    const full = year < 100 ? 2000 + year : year;
    // Zwanzig Jahre zurück, ein Jahr voraus: alles andere ist eine Kundennummer.
    if (full < now.getFullYear() - 20 || full > now.getFullYear() + 1) return;
    const date = new Date(full, month - 1, day, 12);
    if (date.getMonth() === month - 1 && date.getDate() === day) candidates.push(date.getTime());
  };

  for (const hit of window.matchAll(/\b(\d{1,2})\.\s?(\d{1,2})\.\s?(\d{2,4})\b/g)) {
    push(Number(hit[3]), Number(hit[2]), Number(hit[1]));
  }
  for (const hit of window.matchAll(
    new RegExp(`\\b(\\d{1,2})\\.?\\s+(${Object.keys(MONATE).join('|')})\\s+(\\d{4})\\b`, 'gi'),
  )) {
    push(Number(hit[3]), MONATE[hit[2]!.toLowerCase()] ?? 0, Number(hit[1]));
  }
  for (const hit of window.matchAll(/\b(\d{4})-(\d{2})-(\d{2})\b/g)) {
    push(Number(hit[1]), Number(hit[2]), Number(hit[3]));
  }

  return candidates[0] ?? null;
}

// --- Word ------------------------------------------------------------------

/** `.docx` ist ein ZIP mit XML. Auspacken kann die App schon — der Import tut nichts anderes. */
function fromDocx(bytes: Uint8Array): string {
  const files = unzipSync(bytes, { filter: (file) => file.name === 'word/document.xml' });
  const xml = files['word/document.xml'];
  if (!xml) return '';
  return tidy(
    strFromU8(xml)
      // Absätze und Zeilenumbrüche behalten, alles andere ist Auszeichnung.
      .replace(/<w:p[ >]/g, '\n<w:p ')
      .replace(/<w:br\s*\/>/g, '\n')
      .replace(/<w:tab\s*\/>/g, '\t')
      .replace(/<[^>]+>/g, '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code))),
  );
}

// --- E-Mail ----------------------------------------------------------------

/**
 * Nach dem Auflösen von `=C3=BC` stehen einzelne Bytes da, keine Zeichen. Wer sie so
 * stehen lässt, bekommt „GebÃ¼hr" statt „Gebühr" — also noch einmal als UTF-8 lesen.
 */
function reinterpret(binary: string, charset = 'utf-8'): string {
  if (!/[\u0080-\u00ff]/.test(binary)) return binary;
  try {
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0) & 0xff);
    return new TextDecoder(charset, { fatal: true }).decode(bytes);
  } catch {
    return binary;
  }
}

function decodeQuotedPrintable(body: string): string {
  return reinterpret(
    body
      .replace(/=\r?\n/g, '')
      .replace(/=([0-9A-F]{2})/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16))),
  );
}

/** `=?UTF-8?B?…?=` — kodierte Kopfzeilen, sonst steht Kauderwelsch im Titel. */
function decodeHeader(value: string): string {
  return value.replace(
    /=\?([^?]+)\?([BQ])\?([^?]*)\?=/gi,
    (_, charset: string, kind: string, data: string) => {
      try {
        const raw =
          kind.toUpperCase() === 'B'
            ? Uint8Array.from(atob(data), (char) => char.charCodeAt(0))
            : Uint8Array.from(
                data
                  .replace(/_/g, ' ')
                  .replace(/=([0-9A-F]{2})/gi, (_m, hex: string) =>
                    String.fromCharCode(parseInt(hex, 16)),
                  ),
                (char) => char.charCodeAt(0) & 0xff,
              );
        return new TextDecoder(charset.toLowerCase()).decode(raw);
      } catch {
        return data;
      }
    },
  );
}

function stripHtml(html: string): string {
  return tidy(
    html
      .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|tr|h\d)>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/[ \t]{2,}/g, ' '),
  );
}

/**
 * Eine `.eml` ist Text mit Kopfzeilen. Absender, Betreff und Datum gibt es hier
 * geschenkt — bei einem Scan müsste man sie raten.
 */
export function fromEml(raw: string): Extracted {
  const split = raw.search(/\r?\n\r?\n/);
  const head = (split > 0 ? raw.slice(0, split) : raw).replace(/\r?\n[ \t]+/g, ' ');
  const body = split > 0 ? raw.slice(split).trimStart() : '';

  const header = (name: string): string | null => {
    const hit = new RegExp(`^${name}:\\s*(.*)$`, 'im').exec(head);
    return hit?.[1] ? decodeHeader(hit[1].trim()) : null;
  };

  const encoding = (header('Content-Transfer-Encoding') ?? '').toLowerCase();
  const type = (header('Content-Type') ?? '').toLowerCase();

  let text = body;
  // Mehrteilige Nachrichten: der erste Textteil genügt; Anhänge sind hier nicht das Thema.
  const boundary = /boundary="?([^";\s]+)"?/i.exec(type)?.[1];
  if (boundary) {
    const parts = body.split(new RegExp(`--${boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));
    const plain = parts.find((part) => /content-type:\s*text\/plain/i.test(part));
    const html = parts.find((part) => /content-type:\s*text\/html/i.test(part));
    const chosen = plain ?? html ?? '';
    const inner = chosen.search(/\r?\n\r?\n/);
    text = inner > 0 ? chosen.slice(inner).trim() : chosen;
    if (/content-transfer-encoding:\s*quoted-printable/i.test(chosen)) text = decodeQuotedPrintable(text);
    if (/content-transfer-encoding:\s*base64/i.test(chosen)) {
      try {
        text = new TextDecoder().decode(Uint8Array.from(atob(text.replace(/\s/g, '')), (c) => c.charCodeAt(0)));
      } catch {
        /* dann eben roh */
      }
    }
    if (!plain && html) text = stripHtml(text);
  } else {
    if (encoding === 'quoted-printable') text = decodeQuotedPrintable(text);
    if (type.startsWith('text/html')) text = stripHtml(text);
  }

  const from = header('From');
  const subject = header('Subject');
  const date = header('Date');
  const stamp = date ? Date.parse(date) : Number.NaN;

  // Absender und Betreff gehören mit in den durchsuchbaren Text, nicht nur in den Titel.
  const kopf = [from ? `Von: ${from}` : null, subject ? `Betreff: ${subject}` : null]
    .filter(Boolean)
    .join('\n');

  return {
    text: tidy(`${kopf}\n\n${text}`),
    quelle: 'eml',
    datum: Number.isNaN(stamp) ? findDate(text) : stamp,
    titel: subject ?? firstLine(text),
  };
}

// --- PDF -------------------------------------------------------------------

/**
 * Die Textschicht eines PDFs. `pdfjs` wird erst hier geladen — es ist gut ein
 * Megabyte, und die meisten Sitzungen fassen nie ein PDF an.
 */
export async function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  const pdfjs = await import('pdfjs-dist');
  // Der Worker wird von Vite als eigene Datei ausgeliefert und erst hier geladen.
  // Ohne ihn versucht pdfjs einen Ersatz nachzuladen, was im Bündel fehlschlägt.
  const worker = await import('pdfjs-dist/build/pdf.worker.mjs?url');
  (pdfjs.GlobalWorkerOptions as { workerSrc: string }).workerSrc = worker.default;
  return pdfjs;
}

async function fromPdf(bytes: Uint8Array): Promise<{ text: string; pages: number }> {
  const pdfjs = await loadPdfjs();
  // `bytes.slice()`: pdfjs übergibt die Daten an seinen Arbeiter und **entkoppelt
  // dabei den Speicher** — das Original ist danach unbrauchbar. Wer es anschließend
  // noch hochladen will, bekommt „detached ArrayBuffer" um die Ohren. Genau daran
  // sind PDF-Uploads gescheitert, seit der Arbeiter richtig eingerichtet ist.
  const document = await pdfjs.getDocument({ data: bytes.slice() }).promise;

  const pages: string[] = [];
  for (let index = 1; index <= document.numPages; index += 1) {
    const page = await document.getPage(index);
    const content = await page.getTextContent();
    pages.push(
      content.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
        .replace(/\s{2,}/g, ' '),
    );
  }
  const count = document.numPages;
  await document.cleanup();
  return { text: tidy(pages.join('\n\n')), pages: count };
}

// --- Einstieg --------------------------------------------------------------

function extension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Ohne Dateinamen und ohne Endung entscheidet der Inhaltstyp. */
export function kindOf(name: string, mime: string): Quelle | 'bild' {
  const end = extension(name);
  if (mime === 'application/pdf' || end === 'pdf') return 'pdf';
  if (end === 'docx' || mime.includes('wordprocessingml')) return 'docx';
  if (end === 'eml' || mime === 'message/rfc822') return 'eml';
  if (mime.startsWith('image/')) return 'bild';
  if (mime.startsWith('text/') || ['txt', 'md', 'csv', 'json', 'log'].includes(end)) return 'text';
  return 'keine';
}

/**
 * Holt aus einer Datei heraus, was ohne Erkennung zu holen ist. Was übrig bleibt,
 * bekommt `quelle: 'keine'` — daran erkennt der Bereich, was noch Erkennung braucht.
 */
export async function extract(name: string, mime: string, bytes: Uint8Array): Promise<Extracted> {
  const fallbackTitle = name.replace(/\.[^.]+$/, '') || null;
  const kind = kindOf(name, mime);

  try {
    if (kind === 'text') {
      const text = tidy(decoder.decode(bytes));
      return { text, quelle: 'text', datum: findDate(text), titel: firstLine(text) ?? fallbackTitle };
    }
    if (kind === 'eml') {
      const result = fromEml(decoder.decode(bytes));
      return { ...result, titel: result.titel ?? fallbackTitle };
    }
    if (kind === 'docx') {
      const text = fromDocx(bytes);
      return { text, quelle: 'docx', datum: findDate(text), titel: firstLine(text) ?? fallbackTitle };
    }
    if (kind === 'pdf') {
      const { text, pages } = await fromPdf(bytes);
      // Zu wenig Text für die Seitenzahl heißt: eingescannt, nicht geschrieben.
      if (text.length < pages * TEXT_PER_PAGE) {
        return { text: '', quelle: 'keine', datum: null, titel: fallbackTitle };
      }
      return { text, quelle: 'pdf', datum: findDate(text), titel: firstLine(text) ?? fallbackTitle };
    }
  } catch (error) {
    console.warn('[archiv] Text konnte nicht gewonnen werden:', error);
  }

  return { text: '', quelle: 'keine', datum: null, titel: fallbackTitle };
}
